import React, { useState, useRef, useEffect } from 'react';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

// Quill's default "size" format is class-based (ql-size-large etc.),
// which only renders correctly inside a page that loads Quill's own
// stylesheet — useless in an actual sent email, where no such stylesheet
// exists. Re-registering it as a style-based attributor makes it emit a
// real inline font-size instead, which works in any email client.
//
// No `.whitelist` is set on either attributor: a whitelist doesn't just
// limit the toolbar dropdown, it makes Quill DROP any existing value that
// isn't on the list the moment it parses pasted HTML (e.g. a signature
// using font-size:17px, or any font-family at all, since font wasn't
// registered before) — which is exactly the "loses some of the
// formatting" bug. Leaving both unrestricted preserves whatever value
// content already has; the toolbar's own `size` control below still only
// offers a fixed set of choices to pick FROM, independent of what's
// preserved on parse. Color/background are already style-based and
// unrestricted by default, so no override needed for those.
const SizeStyle = Quill.import('attributors/style/size');
Quill.register(SizeStyle, true);
const FontStyle = Quill.import('attributors/style/font');
Quill.register(FontStyle, true);

// Quill only understands its own format vocabulary (bold/italic/underline/
// lists/links/size/color/background-on-text) — a hand-crafted <div> with
// padding/border/background (a boxed callout, a bordered signature block)
// gets silently flattened the moment Quill parses it. This custom block
// embed protects such divs: they're stored as one opaque unit (exact
// outerHTML preserved) that Quill can move or delete as a whole but can't
// reach inside and simplify. Text *inside* a protected div still can't be
// edited via the Rich text toolbar — only Raw HTML reaches inside it.
//
// Registration is wrapped defensively — if Quill's internals don't expose
// what this expects (e.g. a future Quill version), embedSupportError gets
// set and the component below falls back to a plain-Quill experience with
// complex content simply left uneditable in Rich mode, rather than the
// whole editor breaking.
let embedSupportError = null;
let ProtectedDelta = null;
try {
  const BlockEmbed = Quill.import('blots/block/embed');
  ProtectedDelta = Quill.import('delta');

  class RawHtmlBlot extends BlockEmbed {
    static create(outerHtml) {
      // eslint-disable-next-line no-console
      console.log('[RichBodyEditor] RawHtmlBlot.create called with:', (outerHtml || '').slice(0, 80));
      const wrapper = document.createElement('div');
      wrapper.innerHTML = outerHtml;
      const node = wrapper.firstElementChild || document.createElement('div');
      node.setAttribute('contenteditable', 'false');
      node.classList.add('ql-raw-html-embed');
      return node;
    }

    static value(node) {
      const clone = node.cloneNode(true);
      clone.removeAttribute('contenteditable');
      clone.classList.remove('ql-raw-html-embed');
      // eslint-disable-next-line no-console
      console.log('[RichBodyEditor] RawHtmlBlot.value called, returning:', (clone.outerHTML || '').slice(0, 80));
      return clone.outerHTML;
    }
  }
  RawHtmlBlot.blotName = 'rawHtmlBlock';
  RawHtmlBlot.tagName = 'DIV';
  Quill.register(RawHtmlBlot);
} catch (err) {
  embedSupportError = err;
  console.error('[RichBodyEditor] custom embed registration failed, falling back:', err);
}

const rawHtmlMatcher = (node, delta) => {
  if (embedSupportError) return delta;
  try {
    const marked = node.tagName === 'DIV' && node.getAttribute('data-raw-html') === 'true';
    // eslint-disable-next-line no-console
    console.log('[RichBodyEditor] matcher saw DIV', { marked, snippet: (node.outerHTML || '').slice(0, 80) });
    if (marked) {
      const clone = node.cloneNode(true);
      clone.removeAttribute('data-raw-html');
      return new ProtectedDelta().insert({ rawHtmlBlock: clone.outerHTML });
    }
  } catch (err) {
    console.error('[RichBodyEditor] matcher error, leaving content as-is:', err);
  }
  return delta;
};

const QUILL_MODULES = {
  toolbar: [
    [{ size: ['12px', '14px', false, '18px', '24px', '32px'] }],
    ['bold', 'italic', 'underline'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
  clipboard: embedSupportError ? undefined : { matchers: [['DIV', rawHtmlMatcher]] },
};

const BOX_STYLE_PATTERN = /background|border|padding|box-shadow/i;

// A wrapper div whose only job is shared typography (font-size/line-height/
// color/font-family — no box styling of its own) gets unwrapped so its
// children stay directly editable, with its styles pushed onto each child.
// Any div that does have box styling is left for the marking pass below.
// Wrapped in try/catch — any parse failure just returns the input
// untouched rather than throwing.
const prepareHtmlForQuill = (html) => {
  if (!html || embedSupportError) return html;
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const container = doc.body;

    if (container.children.length === 1 && container.children[0].tagName === 'DIV') {
      const wrapper = container.children[0];
      const wrapperStyle = wrapper.getAttribute('style') || '';
      if (!BOX_STYLE_PATTERN.test(wrapperStyle)) {
        if (wrapperStyle) {
          Array.from(wrapper.children).forEach((child) => {
            const existing = child.getAttribute('style') || '';
            child.setAttribute('style', `${wrapperStyle};${existing}`);
          });
        }
        while (wrapper.firstChild) container.insertBefore(wrapper.firstChild, wrapper);
        wrapper.remove();
      }
    }

    const marked = container.querySelectorAll('div');
    marked.forEach((div) => {
      div.setAttribute('data-raw-html', 'true');
    });
    // eslint-disable-next-line no-console
    console.log('[RichBodyEditor] prepareHtmlForQuill marked', marked.length, 'div(s):', Array.from(marked).map((d) => (d.outerHTML || '').slice(0, 60)));

    return container.innerHTML;
  } catch (err) {
    console.error('[RichBodyEditor] prepareHtmlForQuill failed, using raw value:', err);
    return html;
  }
};

// Cleans up onChange output — strips the live DOM markers (contenteditable,
// our internal class) that end up baked into Quill's root.innerHTML for a
// protected embed, so the saved value doesn't accumulate them.
const cleanOutgoingHtml = (html) => {
  if (!html || embedSupportError) return html;
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    doc.querySelectorAll('.ql-raw-html-embed').forEach((el) => {
      el.removeAttribute('contenteditable');
      el.classList.remove('ql-raw-html-embed');
    });
    return doc.body.innerHTML;
  } catch (err) {
    console.error('[RichBodyEditor] cleanOutgoingHtml failed, using raw value:', err);
    return html;
  }
};

// Shared by ComposerPanel.js (Interconnecting), DirectComposerPanel.js, and
// EmailManagerTab.js (Campaigns). Deliberately NOT a fully-controlled Quill
// component while in Rich mode — re-parsing and re-feeding the whole HTML
// string back into Quill on every keystroke (via a `value` prop that
// changes every time onChange fires) is what caused an earlier version of
// this to hang the page when protected embeds were involved. Instead,
// Quill's content is only reset (via remounting with a new `key`) on
// genuinely external changes — entering Rich mode, or the body changing
// for a reason other than this component's own onChange (AI assist,
// loading a saved template). While the user is just typing, Quill owns its
// own DOM state and only pushes changes outward.
// insertRef (optional): set to a function that inserts text where the
// cursor last was (e.g. a {{firstName}} fill-in clicked from outside).
const RichBodyEditor = ({ value, onChange, placeholder, showRespondLinkButton = false, insertRef = null }) => {
  const [mode, setMode] = useState('rich');
  const quillRef = useRef(null);
  const lastPushedValueRef = useRef(null);
  const prevModeRef = useRef('html');
  const [editorKey, setEditorKey] = useState(0);
  const [richInitialValue, setRichInitialValue] = useState(() => prepareHtmlForQuill(value));
  const textareaRef = useRef(null);
  const lastIndexRef = useRef(null); // cursor position in Quill before it lost focus

  if (insertRef) {
    insertRef.current = (text) => {
      if (mode === 'rich') {
        const editor = quillRef.current?.getEditor();
        if (!editor) return;
        const at = lastIndexRef.current ?? Math.max(0, editor.getLength() - 1);
        editor.insertText(at, text, 'user');
        editor.setSelection(at + text.length, 0, 'user');
        lastIndexRef.current = at + text.length;
      } else {
        const ta = textareaRef.current;
        const v = value || '';
        const start = ta ? ta.selectionStart : v.length;
        const end = ta ? ta.selectionEnd : v.length;
        onChange(v.slice(0, start) + text + v.slice(end));
        requestAnimationFrame(() => { if (ta) { ta.focus(); ta.setSelectionRange(start + text.length, start + text.length); } });
      }
    };
  }

  useEffect(() => {
    const enteringRich = mode === 'rich' && prevModeRef.current !== 'rich';
    const externalChange = mode === 'rich' && value !== lastPushedValueRef.current;
    if (enteringRich || externalChange) {
      setRichInitialValue(prepareHtmlForQuill(value));
      setEditorKey((k) => k + 1);
    }
    prevModeRef.current = mode;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, value]);

  const handleRichChange = (html) => {
    const cleaned = cleanOutgoingHtml(html);
    lastPushedValueRef.current = cleaned;
    onChange(cleaned);
  };

  const handleRespondLink = () => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const selection = editor.getSelection();
    if (!selection || selection.length === 0) {
      window.alert('Highlight some text first, then click this to turn it into the respond link.');
      return;
    }
    editor.formatText(selection.index, selection.length, 'link', '{{respondUrl}}');
  };

  return (
    <div>
      <div className="as-suggestion-mode-toggle" style={{ marginBottom: '0.4rem' }}>
        <button type="button" className={`as-filter-tab ${mode === 'rich' ? 'as-filter-tab--active' : ''}`} onClick={() => setMode('rich')}>
          Rich text
        </button>
        <button type="button" className={`as-filter-tab ${mode === 'html' ? 'as-filter-tab--active' : ''}`} onClick={() => setMode('html')}>
          Raw HTML
        </button>
        {showRespondLinkButton && mode === 'rich' && (
          <button
            type="button"
            className="as-filter-tab"
            onClick={handleRespondLink}
            title="Highlight text, then click this to hyperlink it to the recipient's respond link"
          >
            🔗 Link to Respond
          </button>
        )}
      </div>
      {mode === 'rich' ? (
        <ReactQuill
          key={editorKey}
          ref={quillRef}
          theme="snow"
          defaultValue={richInitialValue || ''}
          onChange={handleRichChange}
          onChangeSelection={(range) => { if (range) lastIndexRef.current = range.index; }}
          modules={QUILL_MODULES}
          placeholder={placeholder}
        />
      ) : (
        <textarea ref={textareaRef} rows={8} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
      )}
    </div>
  );
};

export default RichBodyEditor;
